/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  TextField,
  useMediaQuery,
  useTheme,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useAppDispatch } from "@store/store";
import { TerminosCondiciones } from "@components/TerminosCondiciones";
import {
  PASSWORD,
  HAS_SPECIAL_CHARACTERS,
  HAS_NUMBERS,
  HAS_UPPERCASE,
  HAS_LOWERCASE,
  HAS_WHITESPACE,
} from "@utils/expreciones";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { PayloadRequestColoneApplication } from "@interfaces/MarketPlace";
import { usePostCloneApplicationMutation } from "@api/marketPlaceApis";
import * as Sentry from "@sentry/react";

const CrearCuentaPymio = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [transferCompanyId, setTransferCompanyId] = useState<string>("");
  const [accessToken, setAccessToken] = useState<string>("");
  const [hasCuenta, setHasCuenta] = useState<boolean>(false);
  const [applicationId, setApplicationId] = useState<string>("");

  const [postCloneApplication] = usePostCloneApplicationMutation();

  const formik = useFormik({
    initialValues: {
      password: "",
    },
    validationSchema: yup.object().shape({
      password: yup.string().required("Campo requerido").matches(PASSWORD, "Formato Incorrecto"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        if (values.password !== formik.values.password) {
          return;
        }

        const payload: PayloadRequestColoneApplication = {
          user_id: userId,
          user_name: userName,
          transfer_company_id: transferCompanyId,
          password: formik.values.password,
        };
        await postCloneApplication({ application_id: applicationId, data: payload, accessToken }).unwrap();

        navigate("/producto/creditosimple/expediente_documentos");
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "CrearCuentaPymio-onSubmit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const isLengthValid = formik.values.password.length >= 8 && formik.values.password.length <= 20;
  const hasNumber = HAS_NUMBERS.test(formik.values.password);
  const hasSpecialChar = HAS_SPECIAL_CHARACTERS.test(formik.values.password);
  const hasUpperCase = HAS_UPPERCASE.test(formik.values.password);
  const hasLowerCase = HAS_LOWERCASE.test(formik.values.password);
  const hasWhitespace = HAS_WHITESPACE.test(formik.values.password);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    initialFetch();
  }, []);

  const initialFetch = async () => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const userIdParam = searchParams.get("user_id");
      const companyParam = searchParams.get("company");
      const userNameParam = searchParams.get("userName");
      const transferCompanyIdParam = searchParams.get("transfer_company_id");
      const hasCuentaParam = searchParams.get("hasCuenta");
      const accessTokenParam = searchParams.get("access_token");
      const applicationIdParam = searchParams.get("application_id");

      if (
        !userIdParam &&
        !companyParam &&
        !userNameParam &&
        !transferCompanyIdParam &&
        !accessTokenParam &&
        !hasCuentaParam &&
        !applicationIdParam
      ) {
        return <Navigate to="/" replace />;
      }

      setUserId(userIdParam || "");
      setCompany(companyParam || "");
      setUserName(userNameParam || "");
      setTransferCompanyId(transferCompanyIdParam || "");
      setAccessToken(accessTokenParam || "");
      setHasCuenta(hasCuentaParam === "true");
      setApplicationId(applicationIdParam || "");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Grid container height={"100%"}>
      <Grid item xs={12} md={8} mt={4}>
        <Box
          sx={{
            mt: isMobileOrTablet ? 1 : 2,
            mx: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={theme?.palette?.fontWeights?.bold}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.crear_cuenta?.titulo || "Te damos la bienvenida"}
          </Typography>

          {hasCuenta ? (
            <Typography
              mt={isMobileOrTablet ? 2 : 3}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.main}
            >
              Detectamos que ya tienes una cuenta con {company}, ingresa con tu usuario y contraseña.
            </Typography>
          ) : (
            <Typography
              mt={isMobileOrTablet ? 2 : 3}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.main}
            >
              Crea una contraseña segura para tu cuenta {company} powered by Lendia.
            </Typography>
          )}

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Typography variant={"body1"} color={theme?.palette?.primary?.dark} textAlign={"center"} fontWeight={500}>
                Usuario
              </Typography>
              <Typography variant={"body1"} color={theme?.palette?.primary?.main} textAlign={"center"} fontWeight={600}>
                {userName}
              </Typography>

              <Box>
                <TextField
                  id="password"
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ color: theme?.palette?.primary?.main }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={formik.isSubmitting}
                />
              </Box>
              {!hasCuenta && (
                <Box mt={isMobileOrTablet ? 2 : 3}>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    fontWeight={500}
                    color={theme?.palette?.primary?.dark}
                  >
                    Tu contraseña debe contener:
                  </Typography>

                  <Typography
                    mt={1}
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={isLengthValid ? "green" : "red"}
                    display="flex"
                    alignItems="center"
                  >
                    {isLengthValid ? <CheckIcon /> : <CloseIcon />} &nbsp;Entre 8 y 20 caracteres
                  </Typography>

                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={hasNumber ? "green" : "red"}
                    display="flex"
                    alignItems="center"
                  >
                    {hasNumber ? <CheckIcon /> : <CloseIcon />} &nbsp;Un carácter numérico
                  </Typography>

                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={hasSpecialChar ? "green" : "red"}
                    display="flex"
                    alignItems="center"
                  >
                    {hasSpecialChar ? <CheckIcon /> : <CloseIcon />} &nbsp;Un carácter especial
                  </Typography>

                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={hasUpperCase ? "green" : "red"}
                    display="flex"
                    alignItems="center"
                  >
                    {hasUpperCase ? <CheckIcon /> : <CloseIcon />} &nbsp;Una mayúscula
                  </Typography>

                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={hasLowerCase ? "green" : "red"}
                    display="flex"
                    alignItems="center"
                  >
                    {hasLowerCase ? <CheckIcon /> : <CloseIcon />} &nbsp;Una minúscula
                  </Typography>

                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={!hasWhitespace ? "green" : "red"}
                    display="flex"
                    alignItems="center"
                  >
                    {!hasWhitespace ? <CheckIcon /> : <CloseIcon />} &nbsp;Sin espacios en blanco
                  </Typography>
                </Box>
              )}

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Box>
            </form>
            <Box sx={{ mt: 4, mb: 2 }}>
              <TerminosCondiciones />
            </Box>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default CrearCuentaPymio;
